import {
  BackgroundCheckDataStatus,
  CollectDataStepDeclineReason,
  CollectedDataApprovalType,
  CollectedDataStatus,
  CollectedDataStepType,
  ContractorStepExpirationStatus,
  DocumentUploadOrigin,
} from 'src/constants/collectedData'
import {
  CollectDataStepCreateReason,
  CollectDataStepCreateSource,
  RecollectionType,
} from 'src/constants/contractor/enums'
import { FormItem } from './collectedDataFormItem'
import { CollectDataTemplateStep } from './collectDataTemplateStep'

export interface CollectedDataDocument {
  createdAt: number
  updatedAt: number
  id: string
  filePath: string
  fileName: string
  extension: string
  collectDataStepId?: string
  uploadOrigin?: DocumentUploadOrigin | null
  metadata: null | {
    dob: string
    vin: null
    lastName: string
    firstName: string
    dateIssued: string
    documentId: null
    middleName: null
    issuingAuthority: string
  }
}

export interface BaseCollectedDataStepMetadata {
  title?: string
  description?: string
  name?: string
}

interface BaseCollectedDataStep {
  approvalType?: CollectedDataApprovalType
  createdAt: number
  updatedAt: number
  id: string
  type: CollectedDataStepType
  label: CollectedDataStepType
  order: number
  optional: boolean
  metadata?: BaseCollectedDataStepMetadata | null
  recollectSameTypeSteps: boolean
  backgroundCheckReport?: YardstikReport | null
  createReason?: CollectDataStepCreateReason | null
  createInitiatorSource?: CollectDataStepCreateSource | null
  createInitiatorEmail?: string | null
}

export interface RegularCollectedDataStep extends BaseCollectedDataStep {
  type: Exclude<
    CollectedDataStepType,
    | CollectedDataStepType.Questionnaire
    | CollectedDataStepType.SignContract
    | CollectedDataStepType.Training
    | CollectedDataStepType.InsuranceCoverage
    | CollectedDataStepType.Form
    | CollectedDataStepType.BusinessEntity
    | CollectedDataStepType.VaccinationInformation
    | CollectedDataStepType.InsuranceBeneficiary
    | CollectedDataStepType.LmsTraining
  >
  meta: undefined
}

export interface QuestionnaireCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.Questionnaire
  meta: Array<{
    id: string
    title: string
    type: 'boolean'
  }>
}

export interface FormCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.Form
}

export interface SignContractCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.SignContract
  meta: { docusignTemplateId?: string }
}

export interface TrainingCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.Training
  meta: {
    title?: string
    subtitle?: string
    actions?: Array<{
      title: string
      type: 'select' | 'input' | 'file'
      fieldName: string
    }>
    cards?: Array<{
      title: string
      content: string
    }>
    partnerTrainingCardTemplates?: Array<{
      title: string
      content: string
    }>
  }
}

export interface BusinessEntityCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.BusinessEntity
}

export interface InsuranceBeneficiaryCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.InsuranceBeneficiary
}

export interface VaccinationInformationCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.VaccinationInformation
}

export interface InsuranceCoverageCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.InsuranceCoverage
  metadata: null | {
    // TODO: fix typing. not existing for insurance coverage step
    title?: string
    description?: string
    name?: string
    // ---------

    insurancePolicyProductId: string
    insurancePolicyProduct: {
      createdAt: number
      updatedAt: number
      id: string
      allowOptOut: boolean
      feeFrequency: string
      feeCents: number
      feePercent: string
      insuranceDocusignTemplateId: null | string
      description: string
      explanation: string
      insuranceProductId: string
      insurancePolicyId: string
      insuranceProduct: {
        createdAt: number
        updatedAt: number
        id: string
        name: string
        description: string
        explanation: string
      }
    }
  }
}

export interface MCNumberCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.MCNumber
}

export interface DOTNumberCollectedDataStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.DOTNumber
}

export interface PIICollectionStep extends BaseCollectedDataStep {
  type: CollectedDataStepType.PIICollection
}

export interface LmsTrainingStep extends Omit<BaseCollectedDataStep, 'metadata'> {
  type: CollectedDataStepType.LmsTraining
  metadata: null | {
    lmsTrainingId: string
    lmsTrainingName: string
  }
}

export type CollectedDataStep =
  | QuestionnaireCollectedDataStep
  | SignContractCollectedDataStep
  | TrainingCollectedDataStep
  | FormCollectedDataStep
  | RegularCollectedDataStep
  | BusinessEntityCollectedDataStep
  | VaccinationInformationCollectedDataStep
  | InsuranceBeneficiaryCollectedDataStep
  | InsuranceCoverageCollectedDataStep
  | MCNumberCollectedDataStep
  | DOTNumberCollectedDataStep
  | PIICollectionStep
  | LmsTrainingStep

/**
 * A screening detail as served by `BackgroundCheckScreeningDetailResponseDto`. The API
 * normalizes to camelCase — the vendor's snake_case spellings are not emitted. `name`
 * carries what the vendor calls `detail_type` (e.g. `drug_test`).
 */
export interface YardstikScreeningDetail {
  name: string
  status: string
  providerDetailId?: string | null
  /**
   * Opaque provider vocabulary, not a closed set. The API translates the values a provider
   * expresses in its own terms and passes anything unmapped through as-is, so treat this as
   * a display string — never switch on it or narrow it to a union.
   */
  progress?: string | null
  sampleCollectedOn?: string | null
  sampleReceivedAtLabOn?: string | null
  clinicVisitTimeframeStartsOn?: string | null
  clinicVisitTimeframeEndsOn?: string | null
  clinicVisitExpiredOn?: string | null
  clinicName?: string | null
}

/**
 * Clinic and sample-collection summary for a screening, built by the API from whichever
 * of its details names a clinic. Absent when no detail does.
 */
export interface YardstikScreeningClinic {
  clinic: string
  clinicVisitWindowStartsOn?: string | null
  clinicVisitWindowEndsOn?: string | null
  sampleCollectedOn?: string | null
  sampleReceivedByLabOn?: string | null
}

/**
 * Normalized screening status. Only present for persisted screenings (those with a
 * `providerScreeningId`) — screenings read straight from the stored provider payload
 * carry their status in `providerStatus` instead.
 */
export enum BackgroundCheckScreeningStatus {
  Pending = 'pending',
  Success = 'success',
  Failed = 'failed',
}

/** The background-check vendor's own status vocabulary, as returned in `providerStatus`. */
export enum YardstikScreeningStatus {
  Canceled = 'canceled',
  Clear = 'clear',
  Complete = 'complete',
  Completed = 'completed',
  Consider = 'consider',
  Created = 'created',
  Dispute = 'dispute',
  Exception = 'exception',
  InfoRequested = 'info_requested',
  Pending = 'pending',
  Processing = 'processing',
  Queued = 'queued',
  Suspended = 'suspended',
}

export interface YardstikScreening {
  providerScreeningId?: string
  name?: string
  label?: string
  types?: Array<string>
  /** Normalized status; absent unless the screening is persisted. */
  status?: BackgroundCheckScreeningStatus
  /** The vendor's own status string, verbatim. */
  providerStatus?: YardstikScreeningStatus
  createdAt?: string | null
  submittedAt?: string | null
  /** Canonical ISO-8601 UTC with milliseconds. Parse it — never compare the raw string. */
  completedAt?: string | null
  elapsedTiming?: string | null
  /**
   * Completion, else submission, else creation date — computed by the API. Canonical
   * ISO-8601 UTC with milliseconds, so `2026-01-03T00:00:00Z` now arrives as
   * `2026-01-03T00:00:00.000Z`: same instant, different string. Parse it rather than using
   * it in an equality check, a dedupe, or a render key.
   */
  lastActionDate: string | null
  clinic?: YardstikScreeningClinic
  details: Array<YardstikScreeningDetail> | null
}

export interface YardstikReport {
  id: string
  candidateId: string
  status: string
  applyLink: string | null
  candidateConsented: boolean
  isActive: boolean
  source: string | null
  decision: string | null
  screenings: Array<YardstikScreening> | null
}

export interface CollectDataStepConfig {
  id: string
  order: number
  delayed: boolean
}

interface BaseCollectedData {
  createdAt: number
  updatedAt: number
  id: string
  status: CollectedDataStatus
  expirationStatus?: ContractorStepExpirationStatus | null
  reviewMetadata: {
    declineReason: CollectDataStepDeclineReason
    reviewedBy: string
  } | null
  expiresAt: null
  effectiveAt?: string | null
  approvedAt: string
  finishedAt: string
  deletedAt: null
  documents: Array<CollectedDataDocument>
  isRecollecting?: boolean
  recollectionType?: RecollectionType | null
  recollectionInitiatorEmail?: string | null
  type?: CollectedDataStepType
  dataStatus?: BackgroundCheckDataStatus
  lastRejectionReason?: string
  lastRejectionDocuments?: CollectedDataDocument[]
  skipped?: boolean
  collectDataStepConfig?: CollectDataStepConfig | null
}

export interface RegularCollectedData extends BaseCollectedData {
  collectDataStep: RegularCollectedDataStep
}

export interface QuestionnaireCollectedData extends BaseCollectedData {
  collectDataStep: QuestionnaireCollectedDataStep
}

export interface FormCollectedData extends BaseCollectedData {
  collectDataStep: FormCollectedDataStep
  metadata: {
    formItems: Array<FormItem>
  }
}

export interface SignContractCollectedData extends BaseCollectedData {
  collectDataStep: SignContractCollectedDataStep
}

export interface TrainingCollectedData extends BaseCollectedData {
  collectDataStep: TrainingCollectedDataStep
  metadata: null | {
    dateSubmitted: string
  }
}

export interface BusinessEntityCollectedData extends BaseCollectedData {
  collectDataStep: BusinessEntityCollectedDataStep
  metadata: null | {
    businessName?: string
    city?: string
    state?: string
    streetAddress?: string
    zipCode?: string
  }
}

export interface InsuranceBeneficiaryCollectedData extends BaseCollectedData {
  collectDataStep: InsuranceBeneficiaryCollectedDataStep
  metadata: null | {
    firstName: string
    lastName: string
  }
}

export interface VaccinationInformationCollectedData extends BaseCollectedData {
  collectDataStep: VaccinationInformationCollectedDataStep
  metadata: null | {
    date?: string
    brand?: string
    isVaccinated: boolean
  }
}

export interface InsuranceCoverageCollectedData extends BaseCollectedData {
  collectDataStep: InsuranceCoverageCollectedDataStep
  metadata: null | {
    optOut: boolean
    feeCents: number
    feePercent: string
  }
}

export interface MCNumberCollectedData extends BaseCollectedData {
  collectDataStep: MCNumberCollectedDataStep
  metadata: null | {
    mcNumber?: string
    lastSubmittedAt?: string
  }
}

export interface DOTNumberCollectedData extends BaseCollectedData {
  collectDataStep: DOTNumberCollectedDataStep
  metadata: null | {
    dotNumber?: string
    usDotNumber?: string
    dotNumberStateOfIssue?: string
    lastSubmittedAt?: string
  }
}

export interface PIICollectionCollectedData extends BaseCollectedData {
  collectDataStep: PIICollectionStep
  metadata: null | {
    ssn?: string
    streetAddress?: string
    city?: string
    state?: string
    zipCode?: string
    workAuthorization?: string
  }
}

export interface LmsTrainingCollectedData extends BaseCollectedData {
  collectDataStep: LmsTrainingStep
}

export type CollectedData =
  | RegularCollectedData
  | QuestionnaireCollectedData
  | SignContractCollectedData
  | TrainingCollectedData
  | InsuranceBeneficiaryCollectedData
  | VaccinationInformationCollectedData
  | FormCollectedData
  | BusinessEntityCollectedData
  | InsuranceCoverageCollectedData
  | MCNumberCollectedData
  | DOTNumberCollectedData
  | PIICollectionCollectedData
  | LmsTrainingCollectedData

export interface CollectedDataTemplate {
  createdAt: number
  updatedAt: number
  id: string
  name: string
  collectDataTemplateSteps?: Array<CollectDataTemplateStep>
}
